import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve();
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = resolve;
    document.body.appendChild(script);
  });
};

export default function Checkout() {
  const { companySlug } = useParams();
  const navigate = useNavigate();

  const [checkoutData, setCheckoutData] = useState(null);
  const [cart, setCart] = useState([]);
  const [company, setCompany] = useState(null);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("info"); // Always start with customer info
  const [finalAmount, setFinalAmount] = useState(0);
  const [pricing, setPricing] = useState({
    base: 0,
    discount: 0,
    tax: 0,
    total: 0,
  });

  // Load checkout data from localStorage
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("checkoutData") || "{}");
    if (!data.cart || !data.company) {
      Swal.fire("Error", "Invalid checkout data. Please go back.", "error");
      navigate(-1);
      return;
    }

    setCheckoutData(data);
    setCart(data.cart);
    setCompany(data.company);

    // Load pricing breakdown
    if (data.pricing_breakdown) {
      setPricing(data.pricing_breakdown);
      setFinalAmount(data.pricing_breakdown.total);
    }

    // Prefill logged-in user
    const token = localStorage.getItem("token")?.trim();
    if (token) {
      axios
        .get(`${API_BASE_URL}/customers/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setCustomer({
            name: res.data.name || "",
            email: res.data.email || "",
            phone: res.data.phone || "",
          });
          setIsLoggedIn(true);
        })
        .catch(() => setError("Failed to load profile"));
    }
  }, [navigate]);

  // Recalculate final amount (only add fees if online payment required)
  useEffect(() => {
    if (!company?.accept_online_payments) {
      setFinalAmount(pricing.total);
    } else {
      const platformFee = 11;
      const gatewayFee = pricing.total * 0.15; // Razorpay 15%
      setFinalAmount(pricing.total + platformFee + gatewayFee);
    }
  }, [pricing.total, company]);

  const validateCustomer = () => {
    if (!customer.name.trim()) return "Name is required";
    if (!customer.email.match(/^\S+@\S+\.\S+$/)) return "Valid email required";
    if (!customer.phone.match(/^\+?\d{7,15}$/)) return "Valid phone required";
    return null;
  };

  const handleCustomerSubmit = () => {
    const err = validateCustomer();
    if (err) {
      Swal.fire("Validation Error", err, "warning");
      return;
    }

    if (!company?.accept_online_payments) {
      // Offline booking → proceed directly
      handleBooking();
    } else {
      // Online payment → show payment screen
      setStep("gateway");
    }
  };

  const handleBooking = async () => {
    setLoading(true);
    setStep("processing");

    const payload = {
      customer,
      items: cart,
      total_cents: Number(finalAmount.toFixed(2)),
      gateway: company?.accept_online_payments ? checkoutData.gateway : null,
      tax_percent: checkoutData.tax_percent || 0,
      discount_applied: checkoutData.applied_discount || 0,
    };

    try {
      const res = await axios.post(
        `${API_BASE_URL}/companies/${companySlug}/book`,
        payload
      );

      const { orderId, sessionUrl, booking_id } = res.data;

      if (
        company?.accept_online_payments &&
        checkoutData.gateway === "razorpay" &&
        orderId
      ) {
        await loadRazorpay();
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: Math.round(finalAmount * 100),
          currency: "INR",
          order_id: orderId,
          name: company.name,
          description: "Booking Payment",
          handler: async (response) => {
            try {
              await axios.post(`${API_BASE_URL}/webhook/razorpay`, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                bookingId: booking_id,
              });
              Swal.fire("Success!", "Payment successful!", "success");
              localStorage.removeItem("checkoutData");
              navigate(`/booking-success/${booking_id}`);
            } catch (err) {
              Swal.fire(
                "Payment Failed",
                err.response?.data?.message || "Error",
                "error"
              );
            }
          },
          prefill: {
            name: customer.name,
            email: customer.email,
            contact: customer.phone.replace(/^\+/, ""),
          },
          theme: { color: "#28a745" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else if (checkoutData.gateway === "stripe" && sessionUrl) {
        window.location.href = sessionUrl;
      } else {
        // Offline booking success
        Swal.fire(
          "Booked Successfully!",
          `Booking ID: ${booking_id}`,
          "success"
        );
        localStorage.removeItem("checkoutData");
        navigate(`/booking-success/${booking_id}`);
      }
    } catch (err) {
      Swal.fire(
        "Booking Failed",
        err.response?.data?.message || "Please try again",
        "error"
      );
      setStep("info");
    } finally {
      setLoading(false);
    }
  };

  if (!checkoutData) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p>Loading checkout...</p>
      </Container>
    );
  }

  const platformFee = company?.accept_online_payments ? 11 : 0;
  const gatewayFee = company?.accept_online_payments ? pricing.total * 0.15 : 0;

  return (
    <Container className="py-4">
      <h3>Checkout</h3>
      {error && <Alert variant="warning">{error}</Alert>}

      {/* Step 1: Always collect customer info */}
      {step === "info" && (
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Full Name *</Form.Label>
            <Form.Control
              value={customer.name}
              onChange={(e) =>
                setCustomer({ ...customer, name: e.target.value })
              }
              required
              disabled={loading}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email *</Form.Label>
            <Form.Control
              type="email"
              value={customer.email}
              onChange={(e) =>
                setCustomer({ ...customer, email: e.target.value })
              }
              required
              disabled={loading}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Phone *</Form.Label>
            <Form.Control
              type="tel"
              value={customer.phone}
              onChange={(e) =>
                setCustomer({ ...customer, phone: e.target.value })
              }
              required
              disabled={loading}
            />
          </Form.Group>
          <Button
            variant="primary"
            onClick={handleCustomerSubmit}
            disabled={loading}
            className="w-100"
          >
            {loading ? "Please wait..." : "Continue"}
          </Button>
        </Form>
      )}

      {/* Step 2: Only show payment if online payment is required */}
      {step === "gateway" && company?.accept_online_payments && (
        <div>
          <Alert variant="info">
            <strong>Payment Required</strong>
            <br />
            Using{" "}
            {checkoutData.user_country === "IN"
              ? "Razorpay (India)"
              : "Stripe (International)"}
          </Alert>

          <Table bordered className="mb-4">
            <tbody>
              <tr>
                <td>Service Total</td>
                <td>₹{pricing.base.toFixed(2)}</td>
              </tr>
              {pricing.discount > 0 && (
                <tr className="text-success">
                  <td>Discount Applied</td>
                  <td>-₹{pricing.discount.toFixed(2)}</td>
                </tr>
              )}
              <tr>
                <td>Tax ({checkoutData.tax_percent || 0}%)</td>
                <td>₹{pricing.tax.toFixed(2)}</td>
              </tr>
              <tr>
                <td>
                  <strong>Subtotal</strong>
                </td>
                <td>
                  <strong>₹{pricing.total.toFixed(2)}</strong>
                </td>
              </tr>
              <tr>
                <td>Platform Fee</td>
                <td>₹{platformFee.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Gateway Fee (~15%)</td>
                <td>₹{gatewayFee.toFixed(2)}</td>
              </tr>
              <tr className="table-success">
                <td>
                  <strong>Total Payable</strong>
                </td>
                <td>
                  <strong>₹{finalAmount.toFixed(2)}</strong>
                </td>
              </tr>
            </tbody>
          </Table>

          <Button
            variant="success"
            size="lg"
            onClick={handleBooking}
            disabled={loading}
            className="w-100"
          >
            {loading ? "Processing..." : `Pay ₹${finalAmount.toFixed(2)} Now`}
          </Button>
        </div>
      )}

      {/* Processing screen */}
      {step === "processing" && (
        <div className="text-center py-5">
          <Spinner animation="border" size="lg" />
          <p className="mt-3">
            {company?.accept_online_payments
              ? "Processing your payment..."
              : "Confirming your booking..."}
          </p>
        </div>
      )}
    </Container>
  );
}
